import { useCallback, useEffect } from 'react';
import type {
  DefaultValues,
  FieldValues,
  Path,
  PathValue,
  UseFormProps,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

interface UseFilterFormOptions<TData extends FieldValues>
  extends UseFormProps<TData> {
  isOpen?: boolean;

  onApply: () => void;

  defaultValues?: DefaultValues<TData>; // Prohibit async default values
}

/**
 * Handles the filter form logic, especially synchronizing the form state with URL search parameters
 */
export const useFilterForm = <TData extends FieldValues>({
  isOpen,
  onApply,
  ...formOptions
}: UseFilterFormOptions<TData>) => {
  const isFilterKey = useCallback(
    (key: string | number | symbol): key is Path<TData> =>
      formOptions?.defaultValues ? key in formOptions.defaultValues : false,
    [formOptions?.defaultValues],
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const filterForm = useForm<TData>(formOptions);
  const { reset, setValue, getValues } = filterForm;

  const handleClear = useCallback(async () => {
    reset(formOptions?.defaultValues);
  }, [reset, formOptions?.defaultValues]);

  const handleApply = useCallback(
    (filterData: TData) => {
      for (const [key, filter] of Object.entries(filterData)) {
        if (Array.isArray(filter) && filter.length) {
          searchParams.set(
            key,
            filter
              .map((item) => item.value)
              .sort()
              .join(','),
          );
        } else if (filter && !Array.isArray(filter) && filter !== 'all') {
          searchParams.set(key, filter);
        } else if (
          (filter === 'all' && searchParams.has(key)) ||
          (Array.isArray(filter) && filter.length === 0)
        ) {
          searchParams.delete(key);
        }
      }

      setSearchParams(searchParams);
      onApply();
    },
    [onApply, searchParams, setSearchParams],
  );

  useEffect(() => {
    if (isOpen) {
      for (const [key, value] of searchParams) {
        if (isFilterKey(key)) {
          if (Array.isArray(getValues(key))) {
            setValue(
              key,
              value.split(',').map((val) => ({ value: val })) as PathValue<
                TData,
                Path<TData>
              >,
            );
          } else {
            setValue(key, value as PathValue<TData, Path<TData>>);
          }
        }
      }
    }
  }, [isOpen, searchParams, setValue, getValues, isFilterKey]);

  return {
    filterForm,
    handleClear,
    handleApply,
  };
};
